import React, { useState } from 'react';

import { Alert, Badge, Button, Card, Col, Row, Table } from 'react-bootstrap';
import { css, StyleSheet } from 'aphrodite';
import dayjs from 'dayjs';
import { faPlus, faSync } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useDebounce } from 'use-debounce';

import AddBrandSyncModal from 'components/aws_brand_sync/AddBrandSyncModal';
import { awsBrandSyncStatus, awsBrandSyncStatusOptions } from 'lib/enums/AwsBrandSyncEnums';
import DownloadSyncButton from 'components/aws_brand_sync/DownloadSyncButton';
import Container from 'components/shared/Container';
import { AWS_BRAND_SYNC } from 'lib/networking/endpoints';
import { backgroundGrey1, slateGrey, white } from 'lib/css/colors';
import { DATE_FORMAT_WITH_TIME, DEFAULT_PAGE_SIZE } from 'lib/constants';
import LoadingSpinner from 'components/shared/LoadingSpinner';
import PaginationComponent from 'components/shared/PaginationComponent';
import SearchBar from 'components/shared/SearchBar';
import useGet from 'lib/hooks/useGet';

function AwsBrandSyncView() {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_PAGE_SIZE);
  const [searchTerm, setSearchTerm] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [debouncedSearchTerm] = useDebounce(searchTerm, 500);
  const {
    data: { data: { syncs = [], total_count = 0 } = {} } = {},
    loading,
    error,
    refetch,
  } = useGet(AWS_BRAND_SYNC, {
    limit: itemsPerPage,
    offset: itemsPerPage * (currentPage - 1),
    ...(debouncedSearchTerm && { search: debouncedSearchTerm }),
  });

  const handlePageChange = page => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = size => {
    setItemsPerPage(size);
    setCurrentPage(1);
  };

  const getStatusBadge = status => {
    const statusConfig = {
      [awsBrandSyncStatus.PENDING]: { variant: 'warning', label: 'Pending' },
      [awsBrandSyncStatus.PROCESSING]: { variant: 'info', label: 'Processing' },
      [awsBrandSyncStatus.COMPLETED]: { variant: 'success', label: 'Completed' },
      [awsBrandSyncStatus.FAILED]: { variant: 'danger', label: 'Failed' },
    };

    const config = statusConfig[status] || { variant: 'secondary', label: status };
    return <Badge bg={config.variant}>{config.label}</Badge>;
  };

  const formatDate = date => {
    return date ? dayjs(date).format(DATE_FORMAT_WITH_TIME) : '-';
  };

  const handleSyncCreated = () => {
    setShowAddModal(false);
    refetch();
  };

  return (
    <Container>
      <Row>
        <Col>
          <Card className="border-0 shadow-sm">
            <Card.Header className={css(styles.cardHeader)}>
              <div className="d-flex justify-content-between align-items-center">
                <h4 className="mb-0 text-dark">AWS Brand Sync</h4>
                <div className="d-flex align-items-center gap-3">
                  <div className={css(styles.statsText)}>
                    {loading ? (
                      <span>Loading...</span>
                    ) : (
                      <span>
                        <strong>{total_count.toLocaleString()}</strong> total syncs
                      </span>
                    )}
                  </div>
                  <Button
                    variant="primary"
                    onClick={() => setShowAddModal(true)}
                    className="d-flex align-items-center gap-2"
                  >
                    <FontAwesomeIcon icon={faPlus} className="mr-1" />
                    New Sync
                  </Button>
                </div>
              </div>
            </Card.Header>

            <Card.Body className="p-0">
              {/* Search Bar */}
              <div className={css(styles.searchContainer)}>
                <Row>
                  <Col md={6}>
                    <SearchBar
                      value={searchTerm}
                      setValue={setSearchTerm}
                      placeholder="Search by name..."
                    />
                  </Col>
                  <Col md={6} className="d-flex justify-content-end align-items-center">
                    <Button
                      variant="outline-secondary"
                      onClick={() => refetch()}
                      className="d-flex align-items-center gap-2"
                    >
                      <FontAwesomeIcon icon={faSync} className="mr-1" />
                      Refresh
                    </Button>
                  </Col>
                </Row>
              </div>

              {/* Loading State */}
              {loading && <LoadingSpinner />}

              {/* Error State */}
              {error && (
                <Alert variant="danger" className="m-3">
                  Failed to load sync records. Please try again.
                </Alert>
              )}

              {/* Empty State */}
              {!loading && !error && syncs.length === 0 && (
                <div className="text-center py-5 text-muted">
                  <p className="mb-3">No sync records found.</p>
                  <Button variant="primary" onClick={() => setShowAddModal(true)}>
                    <FontAwesomeIcon icon={faPlus} className="mr-1" />
                    Create Your First Sync
                  </Button>
                </div>
              )}

              {/* Data Table */}
              {!loading && !error && syncs.length > 0 && (
                <>
                  <div className="table-responsive">
                    <Table hover className="mb-0">
                      <thead className={css(styles.tableHeader)}>
                        <tr>
                          <th>ID</th>
                          <th>Name</th>
                          <th>Status</th>
                          <th>Brand Count</th>
                          <th>Created By</th>
                          <th>Created At</th>
                          <th>Completed At</th>
                          <th>Download</th>
                        </tr>
                      </thead>
                      <tbody>
                        {syncs.map(sync => (
                          <tr key={sync.id}>
                            <td>{sync.id}</td>
                            <td className="text-truncate" style={{ maxWidth: '200px' }}>
                              {sync.name}
                            </td>
                            <td>
                              {getStatusBadge(
                                awsBrandSyncStatusOptions.find(
                                  option => option.value === sync.status,
                                )?.label,
                              )}
                            </td>
                            <td>{sync.brand_count}</td>
                            <td>{sync.created_by?.name || '-'}</td>
                            <td>{formatDate(sync.date_created)}</td>
                            <td>{formatDate(sync.completed_at)}</td>
                            <td>
                              {sync.source_file_location ? (
                                <DownloadSyncButton syncId={sync.id} syncName={sync.name} />
                              ) : (
                                '-'
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>

                  {/* Pagination */}
                  {total_count > 0 && (
                    <div className="d-flex justify-content-end p-3 border-top bg-light">
                      <PaginationComponent
                        currentPage={currentPage}
                        itemsPerPage={itemsPerPage}
                        totalItems={total_count}
                        setCurrentPage={handlePageChange}
                        setItemsPerPage={handlePageSizeChange}
                      />
                    </div>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Add Brand Sync Modal */}
      <AddBrandSyncModal
        show={showAddModal}
        onHide={() => setShowAddModal(false)}
        onSuccess={handleSyncCreated}
      />
    </Container>
  );
}

const styles = StyleSheet.create({
  cardHeader: {
    backgroundColor: white,
    borderBottom: 'none',
    paddingBottom: 0,
  },
  searchContainer: {
    backgroundColor: backgroundGrey1,
    borderRadius: '8px',
    padding: '1rem',
    margin: '1rem',
  },
  statsText: {
    fontSize: '14px',
    color: slateGrey,
    marginRight: '1rem',
  },
  tableHeader: {
    backgroundColor: backgroundGrey1,
  },
});

export default AwsBrandSyncView;
